import { gql } from '@apollo/client';

export const GET_FORMS = gql`
  query GetForms {
    orgLogo
    vm {
      forms {
        formId
        title
        description
        successMessage
        isListed
        allowMultipleSubmissions
        dateClosed
        dateCreated
        dateLastUpdated
        order
        fieldMappings
        manager {
          managerId
          profile {
            preferredName
          }
        }
        submissionStats {
          totalSubmissions
        }
        formItems {
          __typename
          ... on VOLUNTEER_FormItemFieldType {
            formItemId
            order
            field {
              taskFieldId
              name
            }
          }
        }
        notificationConfig {
          formNotificationConfigId
          notifyAdmins
          notifySpecificManagers
          customEmail
          isEnabled
          managers {
            managerId
            profile {
              preferredName
              lastName
              email
            }
          }
        }
      }
    }
  }
`;

export const GET_FORM = gql`
  query GetForm($formId: String!) {
    vm {
      form(formId: $formId) {
        formId
        title
        description
        successMessage
        redirectUrl
        isListed
        dateClosed
        closedMessage
        allowMultipleSubmissions
        order
        dateCreated
        dateLastUpdated
        manager {
          managerId
          profile {
            preferredName
          }
        }
        formItems {
          __typename
          ... on VOLUNTEER_FormItemFieldType {
            formItemId
            order
            optional
            triggerItem {
              formTriggerItemId
              order
              trigger {
                ... on VOLUNTEER_DynamicDropdownFieldFormTriggerType {
                  triggerOption {
                    dropdownFieldOptionId
                  }
                }
                ... on VOLUNTEER_DynamicCheckboxFieldFormTriggerType {
                  triggerOption {
                    checkboxFieldOptionId
                  }
                }
                ... on VOLUNTEER_DynamicMultipleChoiceFieldFormTriggerType {
                  triggerOption {
                    multipleChoiceFieldOptionId
                  }
                }
                ... on VOLUNTEER_DynamicQuizFieldFormTriggerType {
                  triggerOption {
                    quizTaskFieldOptionId
                  }
                }
              }
            }
            field {
              __typename
              taskFieldId
              name
              description
              ... on VOLUNTEER_ShortTextTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_LongTextTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_NumberTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_EmailTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_PhoneTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_DateTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_AddressTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_AttachmentTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_SignatureTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_FormMultipleChoiceTaskFieldType {
                taskFieldId
                name
                description
                dynamic
                multipleChoiceOptions {
                  multipleChoiceFieldOptionId
                  name
                  order
                  allowText
                  trigger {
                    items {
                      item {
                        ... on VOLUNTEER_FormItemFieldType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemHeadingType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemContentType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemMediaType {
                          formItemId
                        }
                      }
                      order
                    }
                  }
                }
              }
              ... on VOLUNTEER_FormCheckboxTaskFieldType {
                taskFieldId
                name
                description
                dynamic
                checkboxOptions {
                  checkboxFieldOptionId
                  name
                  order
                  allowText
                  trigger {
                    items {
                      item {
                        ... on VOLUNTEER_FormItemFieldType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemHeadingType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemContentType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemMediaType {
                          formItemId
                        }
                      }
                      order
                    }
                  }
                }
              }
              ... on VOLUNTEER_FormDropdownTaskFieldType {
                taskFieldId
                name
                description
                dynamic
                dropdownOptions {
                  dropdownFieldOptionId
                  name
                  order
                  allowText
                  trigger {
                    items {
                      item {
                        ... on VOLUNTEER_FormItemFieldType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemHeadingType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemContentType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemMediaType {
                          formItemId
                        }
                      }
                      order
                    }
                  }
                }
              }
              ... on VOLUNTEER_FormQuizTaskFieldType {
                taskFieldId
                name
                description
                dynamic
                quizTaskFieldOptions {
                  quizTaskFieldOptionId
                  name
                  order
                  allowText
                  trigger {
                    items {
                      item {
                        ... on VOLUNTEER_FormItemFieldType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemHeadingType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemContentType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemMediaType {
                          formItemId
                        }
                      }
                      order
                    }
                  }
                }
                quizTaskFieldCorrect {
                  quizTaskFieldCorrectId
                  correctText
                  quizTaskFieldOption {
                    quizTaskFieldOptionId
                  }
                }
              }
              ... on VOLUNTEER_FormMatrixTaskFieldType {
                taskFieldId
                name
                description
                rows {
                  matrixRowId
                  name
                  order
                }
                columns {
                  matrixColumnId
                  name
                  order
                }
              }
              ... on VOLUNTEER_RatingTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_TimeTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_UrlTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_YesNoTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_LinearScaleTaskFieldType {
                taskFieldId
                name
                description
                minValue
                maxValue
                minLabel
                maxLabel
              }
              ... on VOLUNTEER_NpsTaskFieldType {
                taskFieldId
                name
                description
              }
            }
          }
          ... on VOLUNTEER_FormItemHeadingType {
            formItemId
            order
            heading
          }
          ... on VOLUNTEER_FormItemContentType {
            formItemId
            order
            content
          }
          ... on VOLUNTEER_FormItemMediaType {
            formItemId
            order
            media {
              taskMediaId
              name
              description
              ... on VOLUNTEER_YoutubeMediaType {
                videoId
              }
              ... on VOLUNTEER_VimeoMediaType {
                videoId
              }
            }
          }
        }
        submissionStats {
          totalSubmissions
        }
        notificationConfig {
          formNotificationConfigId
          notifyAdmins
          notifySpecificManagers
          customEmail
          isEnabled
          managers {
            managerId
            profile {
              preferredName
              lastName
              email
            }
          }
        }
      }
    }
  }
`;

export const GET_FORM_SUBMISSIONS_LIST = gql`
  query GetFormSubmissionsList($formId: String!, $offset: Int!, $limit: Int!, $sortOrder: String) {
    vm {
      form(formId: $formId) {
        formId
        title
        formItems {
          __typename
          ... on VOLUNTEER_FormItemFieldType {
            formItemId
            order
            field {
              __typename
              taskFieldId
              name
              ... on VOLUNTEER_LinearScaleTaskFieldType {
                minValue
                maxValue
                minLabel
                maxLabel
              }
            }
          }
        }
        formSubmissions(offset: $offset, limit: $limit, sortOrder: $sortOrder) {
          formSubmissionId
          submissionDate
          commentCount
          creationCount
          formFieldValues {
            formFieldValueId
            taskFieldId
            fieldName
            fieldType
            value
          }
        }
        submissionStats {
          totalSubmissions
        }
      }
    }
  }
`;

export const GET_FORM_SUBMISSION = gql`
  query GetFormSubmission($formId: String!, $submissionId: String!) {
    vm {
      form(formId: $formId) {
        formId
        title
        formSubmission(submissionId: $submissionId) {
          formSubmissionId
          submissionDate
          ipAddress
          userAgent
          referrer
          formFieldValues {
            formFieldValueId
            taskFieldId
            fieldName
            fieldType
            value
          }
          comments {
            commentId
            parentId
            content
            submittedDate
            removedDate
            attachments {
              attachmentId
              fileType
              url
              name
            }
            volunteer {
              volunteerId
              profile {
                preferredName
                lastName
                avatarUrl
              }
            }
            reactions {
              reactionId
              emoji
              volunteer {
                volunteerId
              }
            }
          }
          creations {
            formSubmissionCreationId
            entityType
            entityId
            createdAt
            createdByProfile {
              preferredName
              lastName
            }
          }
        }
        submissionStats {
          totalSubmissions
        }
      }
    }
  }
`;
